import React, { useCallback, useEffect, useRef, useState } from 'react';
import { FlexGrid, FlexGridItem } from 'baseui/flex-grid';
import { useStyletron } from 'baseui';
import { BlockProps } from 'baseui/block';
import { TwoWayMessagingAdditionalInfo } from '@app/two-way/relay-messenger/components/relay-messenger-additional-info/RelayMessengerAdditionalInfo';
import { useClientInfo } from '../../context/ClientInfoContext';
import {
  Client,
  Conversation,
  Message,
  Paginator,
  Participant,
} from '@twilio/conversations';
import { AgentAvatar } from '../agent-avatar/AgentAvatar';
import {
  checkAuthorIsAgent,
  TwoWayChatCard,
  TwoWayChatMessage,
} from '../relay-messenger-card/RelayMessengerChatCard';
import {
  RelayMessengerChatTypes,
  RelayMessengerConversations,
} from '../relay-messenger-conversations/RelayMessengerConversations';
import {
  AgentStatusT,
  DecryptedMessages,
  GetChannelInfoOutput,
  getTagNameById,
  TwoWayService,
} from '../relay-messenger-axios-service/relay-messenger-axios-service';
import { TwoWayMessagingCustomerInfo } from '../customer-info/RelayMessengerCustomerInfo';
import { useCustomerInfo } from '../../context/CustomerInfoContext';
import { useUserInfo } from '../../context/UserInfoContext';
import { useCustomerStatus } from '../../context/CustomerStatusContext';
import { CustomerStatusBadge } from '../customer-status-badge/CustomerStatusBadge';
import { useTwilioClient } from '../../context/TwilioClientContext';
import * as _ from 'lodash';
import { TwoWayChatInput } from '../relay-messenger-chat-input/RelayMessengerChatInput';
import {
  ParticipantConversation,
  ParticipantUpdatedEvent,
  TwilioConversation,
  TwoWayClosedChannel,
  TwoWayConversationClientEvents,
  TwoWayConversationMessageEvents,
  TwoWayUnassignedChannel,
} from '@app/two-way/twilio-conversation.types';
import { Badge } from '../badge/Badge';
import { UserService } from '../user-axios-service/user-axios-service';
import { Customer } from '@app/core/models/customer';
import { useAssignedAgents } from '../../context/AssignedAgentsContext';
import { useNewlyAssignedConvSids } from '../../context/NewlyAssignedConvSidsContext';
import { RelayMessengerAgents } from '../relay-messenger-agents/RelayMessengerAgents';
import { useRefreshConversations } from '../../context/RefreshConversationsContext';
import { useFeatureFlags } from '../../context/FeatureFlagsContext';
import { RelayMessengerAgent } from '../agent-modal/AgentModal';
import { useClosedReasons } from '../../context/ClosedReasonsContext';
import { useAlertMessage } from '../../hooks/useAlertMessage/use-alert-message';
import { useLaunchedBy } from '../../context/LaunchedByListContext';
import { useBulkSelection } from '../../context/BulkSelectionContext';
import { RelayMessengerNavLink } from '../relay-messenger-nav-link/RelayMessengerNavLink';
import { useNavigate } from 'react-router-dom';
import { ConversationType } from '../relay-messenger-container/RelayMessengerContainer';
import { getCustomerInfoByChatType } from '../relay-messenger-list/RelayMessenger.list';
import { useAgentTags } from '../../context/AgentTagsContext';

const itemProps: BlockProps = {
  height: 'scale1000',
  display: 'flex',
  backgroundColor: '#F5F6F6',
};

enum TwoWayTestId {
  MyConversations = 'my-conversations',
  ClosedConversations = 'closed-conversations',
  UnassignedConversations = 'unassigned-conversations',
}

export type TwoWayCustomer = {
  lastPing?: string; // datetime
  middle_name?: string;
  is_generic?: boolean;
  gender?: string;
} & Customer;

export const RelayMessengerHome = ({
  conversationType,
  routeChannelSid,
  routeAgentId,
}: {
  conversationType: ConversationType;
  routeChannelSid?: string;
  routeAgentId?: string;
}) => {
  const featureFlags = useFeatureFlags();
  const [css] = useStyletron();
  const [{ clientId, rmTranslationsEnabled: isRMTranslationsEnabled }] =
    useClientInfo();
  const [{ userId, userFullName, userRoleIds }, setUserInfo] = useUserInfo();
  const [agentJWT, setAgentJWT] = useState('');
  const [agentStatus, setAgentStatus] = useState<AgentStatusT>('inactive');
  const { setRefreshConversations } = useRefreshConversations();
  const isAssignedView = conversationType === 'assigned';
  const isUnassignedView = conversationType === 'unassigned';
  const isClosedView = conversationType === 'closed';
  const isAgentView = conversationType === 'agent';
  const isAssignedViewRef = useRef(isAssignedView);
  useEffect(() => {
    isAssignedViewRef.current = isAssignedView;
  }, [isAssignedView]);

  // Agents List
  const [selectedAgentInfo, setSelectedAgentInfo] =
    useState<RelayMessengerAgent | null>(null);
  const selectedAgentId = isAgentView ? routeAgentId ?? null : null;
  const selectedAgentFullName = selectedAgentInfo?.fullName ?? null;

  const [closedConversations, setClosedConversations] = useState<
    TwoWayClosedChannel[]
  >([]);
  const [allUnassignedChats, setAllUnassignedChats] = useState<
    TwoWayUnassignedChannel[]
  >([]);
  const [allAssignedChats, setAllAssignedChats] = useState<
    TwilioConversation[]
  >([]);
  const [allAgentsChats, setAllAgentsChats] = useState<
    ParticipantConversation[]
  >([]);
  const [isAgentConversationsLoading, setIsAgentConversationsLoading] =
    useState(false);
  const [isAssignedConversationsLoading, setIsAssignedConversationsLoading] =
    useState(false);
  const { agentTags, hasLoadedAgentTags } = useAgentTags();
  const { twilioClient, setTwilioClient } = useTwilioClient();
  const [lastMessageLoaded, setLastMessageLoaded] = useState(false);
  const { customerInfo, setCustomerInfo } = useCustomerInfo();
  const { firstName, lastName, channelSid, customerId, tagName, tagId } =
    customerInfo;
  const [customer, setCustomer] = useState<TwoWayCustomer | null>(null);
  const { customerStatus } = useCustomerStatus();
  const { setAssignedAgents, fetchAssignedAgents } = useAssignedAgents();
  const selectedChannelRef = useRef(channelSid);
  const [myConversationsActivity, setMyConversationsActivity] = useState(false);
  const { setNewlyAssignedConvSids } = useNewlyAssignedConvSids();
  const [isAgentJoined, setIsAgentJoined] = useState(false);
  const [joinNavigationTargetSid, setJoinNavigationTargetSid] = useState<
    string | null
  >(null);
  const clientRef = useRef<Client>(null);
  const [isPingTriggered, setIsPingTriggered] = useState(false);
  const { fetchClosedReasons } = useClosedReasons();
  const { fetchLaunchedByList } = useLaunchedBy();
  const [channelInfo, setChannelInfo] = useState<GetChannelInfoOutput>(); // check this state
  const channelInfoRef = useRef(channelInfo);
  const [translation, setTranslation] = useState<{
    originalMessage: string;
    rawTranslation: string;
    translatedMessage: string;
    edited: boolean;
  } | null>(null);
  const showMessage = useAlertMessage();
  const [enableTranslationError, setEnableTranslationError] = useState<{
    error: boolean;
    message: string;
  }>({
    error: false,
    message: '',
  });

  useEffect(() => {
    selectedChannelRef.current = channelSid;
  }, [channelSid]);

  useEffect(() => {
    // Wait for agentTags to load
    if (!hasLoadedAgentTags || agentTags.length === 0 || !tagId) {
      return;
    }

    const resolvedTagName = getTagNameById(agentTags, tagId);
    if (!resolvedTagName || resolvedTagName === tagName) {
      return;
    }

    setCustomerInfo({
      ...customerInfo,
      tagName: resolvedTagName,
    });
  }, [
    agentTags,
    customerInfo,
    hasLoadedAgentTags,
    setCustomerInfo,
    tagId,
    tagName,
  ]);

  const audioRef = useRef<HTMLAudioElement>(null);
  const playNotificationSound = () => {
    if (audioRef.current) {
      audioRef.current.play();
    }
  };
  const newMessageNotifier = (
    message: Message,
    currentUser: string,
    selectedConversationSid: string,
  ) => {
    if (isAssignedViewRef.current && message.conversation.status === 'joined') {
      if (
        message.author !== currentUser &&
        message.conversation.sid !== selectedConversationSid
      ) {
        // case 1: Assigned conversations tab is selected, current conversation is not selected
        playNotificationSound();
      }
    } else if (message.conversation.status === 'joined') {
      // case 2: Unassigned or Closed conversations tab is selected and ConversationStatus is "joined"
      setMyConversationsActivity(true);
      playNotificationSound();
    }
  };

  // Clear selected agent state
  useEffect(() => {
    if (!isAgentView) {
      setSelectedAgentInfo(null);
    }
  }, [isAgentView]);

  useEffect(() => {
    channelInfoRef.current = channelInfo;
  }, [channelInfo]);

  const isCurrentUserAnAdmin = userRoleIds?.includes('two_way_admin');
  // feature flag check
  const isRMAutoResponseEnabled =
    featureFlags.find((flag) => flag.flagName === 'rm_auto_response')
      ?.flagValue ?? false;

  const newConversationNotifier = () => {
    // case 3: Unassigned or Closed conversations tab is selected
    // Should notify for new conversations only for assigned conversations tab
    if (!isAssignedView) {
      setMyConversationsActivity(true);
      playNotificationSound();
    }
  };

  const showCustomerOrAgentInfo: any =
    (isAssignedView &&
      allAssignedChats?.length &&
      firstName) /* check if customerInfo was cleared after closing the conversation */ ||
    (isUnassignedView &&
      allUnassignedChats?.length &&
      firstName) /* For Unassigned conversation tab customer information should be displayed based on the selected conversation only */ ||
    (isClosedView && closedConversations?.length && firstName) ||
    (isAgentView && allAgentsChats?.length && firstName);
  const showCustomerStatus = isAssignedView || isUnassignedView;
  const customerFullName = `${firstName} ${lastName}`;
  const sendTyping = useCallback(async () => {
    if (twilioClient && channelSid) {
      const channel = await TwoWayService.getTwilioConversation(
        twilioClient,
        channelSid,
      );
      channel.typing();
    }
  }, [twilioClient, channelSid]);
  const [message, setMessage] = useState<TwoWayChatMessage | any>([]);
  const messageRef = useRef([] as TwoWayChatMessage[]);
  const [typingMessage, setTypingMessage] = useState('');
  const [totalUnassignedChats, setTotalUnassignedChats] = useState(0);
  const [totalClosedConversations, setTotalClosedConversations] = useState(0);
  const isConversationTranslatedRef = useRef(false);

  useEffect(() => {
    isConversationTranslatedRef.current =
      channelInfo?.translationsEnabled ?? false;
  }, [channelInfo]);

  useEffect(() => {
    messageRef.current = message;
  }, [message]);

  const setTotalUnassignedCount = (totalUnassignedChats: number) => {
    setTotalUnassignedChats(totalUnassignedChats);
  };

  const setTotalClosedCount = (totalClosedConversations: number) => {
    setTotalClosedConversations(totalClosedConversations);
  };

  let twoWayMessage: TwoWayChatMessage | any = [];

  const fetchToken = useCallback(async () => {
    const jwt = await TwoWayService.getToken(clientId);
    if (jwt) {
      setAgentJWT(jwt);
    }
  }, [clientId]);

  const sendAgentMessage = useCallback(
    async (message: string) => {
      const isInputMessageTranslated = translation ? true : false;
      const author = `agent::${clientId}::${userId}`;
      const channel = await TwoWayService.getTwilioConversation(
        clientRef.current,
        channelSid,
      );
      const displayName = await TwoWayService.getDisplayName(
        author,
        clientRef.current,
      );
      const languageCode = channelInfo?.customerLanguage ?? null;
      const versions = isInputMessageTranslated
        ? {
            original: translation.originalMessage,
            originalLanguageCode: 'en',
            translations: {
              [languageCode]: {
                languageCode: languageCode,
                text: translation.translatedMessage,
                rawTranslation: translation.rawTranslation,
                edited: translation.edited,
              },
            },
          }
        : {
            original: message,
            originalLanguageCode: null,
            translations: {},
          };
      const messageBody = {
        message: {
          text: isInputMessageTranslated
            ? translation.translatedMessage
            : message,
          textLanguageCode: isInputMessageTranslated ? languageCode : null,
          versions,
        },
        channelSid: channelSid,
      };
      setMessage((val) => [
        ...val,
        {
          author: author,
          displayName: displayName,
          body: isInputMessageTranslated
            ? translation.originalMessage
            : message,
          timestamp: new Date().toISOString(),
          versions,
        },
      ]);
      const encryptMessage = await TwoWayService.sendMessageToEncrypt(
        clientId,
        messageBody,
        channelSid,
      );
      await TwoWayService.setAgentStatus(clientId, userId, 'message');
      setAgentStatus('active');
      TwoWayService.sendChannelMessage(
        channel,
        encryptMessage.message,
        customer.ccid,
      );
      setTranslation(null);
    },
    [clientId, userId, channelSid, clientRef, message, twoWayMessage],
  );

  const translateAgentMessage = useCallback(
    async (message: string) => {
      try {
        const translateMessage = await TwoWayService.translateMessage(
          clientId,
          channelSid,
          message,
        );
        setTranslation({
          originalMessage: message,
          rawTranslation: translateMessage,
          translatedMessage: translateMessage,
          edited: false,
        });
      } catch (error) {
        showMessage('Error translating message', 'error');
        console.error('Error translating message:', error);
        setTranslation(null);
      }
    },
    [clientId, userId, channelSid, clientRef, message, twoWayMessage],
  );

  const fetchAgentStatus = useCallback(async () => {
    const agentStatus = await TwoWayService.getAgentStatus(clientId, userId);
    if (agentStatus) {
      setAgentStatus(agentStatus);
    }
  }, [clientId, userId]);

  useEffect(() => {
    fetchToken();
    fetchAgentStatus();
  }, [clientId, userId]);

  const loadConversation = useCallback(async () => {
    try {
      setLastMessageLoaded(false); // To load skeleton loader until last message is received or until setLastMessages(true)
      setEnableTranslationError({
        error: false,
        message: '',
      });
      // show page blank when getMessages or getConversation fails and show additionalInfo
      if (clientId && channelSid && twilioClient) {
        setMessage([]); // setting the messages array to be empty between different conversation clicks and then fetch the current conversation's messages array. The idea is to avoid any scenario where the messages array from a different conversation is shown on the new conversation click.
        setChannelInfo(undefined); // clear the previous conversation's translation info so nothing (e.g. the ping language selector) acts on stale data while the new channel's info is loading
        const channel = await TwoWayService.getTwilioConversation(
          twilioClient,
          channelSid,
        );
        await getChannelInfo();
        if (
          channel &&
          channel.attributes &&
          typeof channel.attributes === 'object' &&
          'customer' in channel.attributes &&
          (channel.attributes.customer as any)?.id === customerId &&
          channelSid === channel.sid
        ) {
          const messages = await getConversationMessage(channel);
          //translation only if translations not present and isRMTranslationsEnabled, isConversationTranslated is true and message is from customer
          if (
            isRMTranslationsEnabled &&
            isAssignedViewRef.current &&
            isConversationTranslatedRef.current
          ) {
            try {
              await translateCustomerMessages(messages, channelSid);
              setMessage(messages);
            } catch (error) {
              setMessage(messages);
              setChannelInfo({ ...channelInfo, translationsEnabled: false });
              setEnableTranslationError({
                error: true,
                message: error.response.data.message,
              });
            }
          } else {
            setMessage(messages);
          }
        }
      }
    } catch (error) {
      setLastMessageLoaded(true); // setting setLastMessageLoaded value to true so the skeleton loader doesn't go into a continuous loop for current issue to fetch messages, which is failing for specific participants. To be removed once Twilio fixes the permissions issue.
    }
  }, [
    clientId,
    channelSid,
    twilioClient,
    customer?.id,
    showCustomerOrAgentInfo,
    selectedChannelRef,
  ]);

  const getChannelInfo = async () => {
    const requestedChannelSid = selectedChannelRef.current;
    const channelInfoData = await TwoWayService.getChannelInfo(
      clientId,
      requestedChannelSid,
    );
    if (selectedChannelRef.current !== requestedChannelSid) {
      // a different conversation was selected while this request was in
      // flight; discard the response instead of overwriting the newer channel's info
      return;
    }
    setChannelInfo({
      translationsEnabled: channelInfoData?.translationsEnabled ?? false,
      customerLanguage: channelInfoData?.customerLanguage,
    });
  };

  const getConversationMessage = async (channel: Conversation) => {
    let pageSize = 100;
    let anchor = 0;
    let hasNextPage = true;
    let allMessages = [];

    try {
      while (hasNextPage && selectedChannelRef.current === channel.sid) {
        const messages = await TwoWayService.getMessages(
          channel,
          pageSize,
          anchor,
          'forward',
        );
        if (messages?.items?.length > 0) {
          const conversationMessages = await addConversationMessages(messages);
          allMessages = [...allMessages, ...conversationMessages]; // Collect messages
        }
        hasNextPage = messages.hasNextPage;
        anchor += pageSize;
      }
      if (selectedChannelRef.current === channel.sid) {
        setLastMessageLoaded(true);
        if (isAssignedViewRef.current) {
          await TwoWayService.setAllMessagesConsumed(channel);
        }
      }
    } catch (error) {
      console.error('Error getting messages', error);
      if (selectedChannelRef.current === channel.sid) {
        setLastMessageLoaded(true);
      }
    }
    return allMessages;
  };

  const getDecryptedVersions = (
    decryptedArr: DecryptedMessages[] | null | undefined,
    encryptedText: string,
  ) => {
    const { versions } = decryptedArr?.find(
      (mess) => mess.cypher === encryptedText,
    ) ?? { versions: null };
    return versions;
  };

  const messageConversationAddedHandler = useCallback(
    async (message: Message) => {
      const currentUser = `agent::${clientId}::${userId}`;
      const selectedChannelSid = selectedChannelRef.current;
      newMessageNotifier(message, currentUser, selectedChannelSid);

      if (
        selectedChannelRef.current &&
        message.conversation.sid === selectedChannelRef.current &&
        isAssignedViewRef.current
      ) {
        await TwoWayService.setAllMessagesConsumed(message.conversation);
      }
      if (
        selectedChannelRef.current &&
        selectedChannelRef.current === message.conversation.sid &&
        message.author !== currentUser &&
        clientRef.current
      ) {
        const encryptedMessage = [];
        encryptedMessage.push({
          cypher: message.body,
          ...(message.attributes && {
            version:
              message.attributes['version'] === 'v4' || message.attributes['v4']
                ? 'v4'
                : message.attributes['v3']
                ? 'v3'
                : 'v2',
          }),
        });
        const decryptedData = await TwoWayService.decryptChannelMessages(
          encryptedMessage,
          clientId,
          selectedChannelSid,
        );
        const displayName = await TwoWayService.getDisplayName(
          message.author,
          clientRef.current,
        );
        let messageObj = {};
        const versions = getDecryptedVersions(
          decryptedData?.decryptedMessages,
          message.body,
        );
        if (
          _.get(message, 'attributes.version') === 'v4' ||
          (_.get(message, 'attributes.v4.encrypted') && decryptedData)
        ) {
          messageObj = {
            sid: message.sid,
            author: message.author,
            displayName: displayName,
            body: versions?.original,
            timestamp: (message.dateUpdated || '').toString(),
            versions: versions,
          };
        } else if (_.get(message, 'attributes.v3.encrypted') && decryptedData) {
          messageObj = {
            sid: message.sid,
            author: message.author,
            displayName: displayName,
            body: versions?.original,
            timestamp: (message.dateUpdated || '').toString(),
          };
        } else if (_.get(message, 'attributes.v2.encrypted') && decryptedData) {
          messageObj = {
            sid: message.sid,
            author: message.author,
            displayName: displayName,
            body: versions?.original,
            timestamp: (message.dateUpdated || '').toString(),
          };
        } else {
          messageObj = {
            sid: message.sid,
            author: message.author,
            displayName: displayName,
            body: message.body,
            timestamp: message.dateUpdated,
          };
        }
        if (
          isRMTranslationsEnabled &&
          isAssignedViewRef.current &&
          isConversationTranslatedRef.current &&
          checkAuthorIsAgent(message.author) === false &&
          !versions?.translations['en']?.text
        ) {
          try {
            await translateCustomerMessages(
              [messageObj],
              message.conversation.sid,
            );
          } catch (error) {
            setMessage((val) => [...val, messageObj]);
            setChannelInfo({ ...channelInfo, translationsEnabled: false });
            setEnableTranslationError({
              error: true,
              message: error.response.data.message,
            });
          }
        } else {
          setMessage((val) => [...val, messageObj]);
        }
      }
    },
    [clientRef, clientId, selectedChannelRef, isAssignedViewRef, channelInfo],
  );
  const messageConversationUpdatedHandler = useCallback(
    async (update: { message: Message; updateReasons: string[] }) => {
      const { message } = update;
      const selectedChannelSid = selectedChannelRef.current;

      // Only handle updates for the active conversation
      if (
        !selectedChannelSid ||
        selectedChannelSid !== message.conversation.sid ||
        !clientRef.current
      ) {
        return;
      }

      const encryptedMessage = [
        {
          cypher: message.body,
          ...(message.attributes && {
            version:
              message.attributes['version'] === 'v4' || message.attributes['v4']
                ? 'v4'
                : message.attributes['v3']
                ? 'v3'
                : 'v2',
          }),
        },
      ];

      const decryptedData = await TwoWayService.decryptChannelMessages(
        encryptedMessage,
        clientId,
        selectedChannelSid,
      );

      const displayName = await TwoWayService.getDisplayName(
        message.author,
        clientRef.current,
      );

      let updatedMessageObj;
      const versions = getDecryptedVersions(
        decryptedData?.decryptedMessages,
        message.body,
      );
      if (
        _.get(message, 'attributes.version') === 'v4' ||
        (_.get(message, 'attributes.v4.encrypted') && decryptedData)
      ) {
        updatedMessageObj = {
          sid: message.sid,
          author: message.author,
          displayName,
          body: versions?.original,
          timestamp: (message.dateUpdated || '').toString(),
          versions: versions,
        };
      } else if (_.get(message, 'attributes.v3.encrypted') && decryptedData) {
        updatedMessageObj = {
          sid: message.sid,
          author: message.author,
          displayName,
          body: versions?.original,
          timestamp: (message.dateUpdated || '').toString(),
        };
      } else if (_.get(message, 'attributes.v2.encrypted') && decryptedData) {
        updatedMessageObj = {
          sid: message.sid,
          author: message.author,
          displayName,
          body: versions?.original,
          timestamp: (message.dateUpdated || '').toString(),
        };
      } else {
        updatedMessageObj = {
          sid: message.sid,
          author: message.author,
          displayName,
          body: message.body,
          timestamp: (message.dateUpdated || '').toString(),
        };
      }

      // ✅ Replace the message in state using sid or add if not found
      setMessage((prevMessages) => {
        const messageIndex = prevMessages.findIndex(
          (msg) => msg.sid === updatedMessageObj.sid,
        );
        if (messageIndex !== -1) {
          // Message found, update it
          const updatedMessages = [...prevMessages];
          updatedMessages[messageIndex] = updatedMessageObj;
          return updatedMessages;
        } else {
          // Message not found, add it
          return [...prevMessages, updatedMessageObj];
        }
      });
    },
    [clientRef, clientId, selectedChannelRef],
  );

  const addConversationMessages = async (messages: Paginator<Message>) => {
    let allMessages = [];
    try {
      const encryptedMessages = messages.items
        .filter(
          (message) =>
            message.attributes['version'] === 'v4' ||
            message.attributes['v4'] ||
            message.attributes['v3'] ||
            message.attributes['v2'],
        )
        .map((message) => ({
          cypher: message.body,
          version:
            message.attributes['version'] === 'v4' || message.attributes['v4']
              ? 'v4'
              : message.attributes['v3']
              ? 'v3'
              : 'v2',
        }));
      const decryptedMessages = await TwoWayService.decryptChannelMessages(
        encryptedMessages,
        clientId,
        channelSid,
      );
      if (messages.items[0].conversation.sid === selectedChannelRef.current) {
        for (let msg of messages.items) {
          const displayName = await TwoWayService.getDisplayName(
            msg.author,
            clientRef.current,
          );
          const isEncrypted =
            _.get(msg, 'attributes.version') === 'v4' ||
            _.get(msg, 'attributes.v4.encrypted') ||
            _.get(msg, 'attributes.v3.encrypted') ||
            _.get(msg, 'attributes.v2.encrypted');
          const versions = getDecryptedVersions(
            decryptedMessages?.decryptedMessages,
            msg.body,
          );
          const decryptedText = isEncrypted ? versions?.original : msg.body;

          allMessages.push({
            sid: msg.sid,
            author: msg.author,
            displayName: displayName,
            body: `${decryptedText}`,
            timestamp: (msg.dateUpdated || '').toString(),
            message: msg,
            versions: versions || null,
          });
        }
      }
    } catch (error) {
      console.error('Error adding message', error);
    }
    return allMessages;
  };

  useEffect(() => {
    if (clientId && channelSid && twilioClient && customerId) {
      loadConversation();
    }
  }, [clientId, channelSid, twilioClient, customerId, showCustomerOrAgentInfo]);

  const channelConversationEventRouter = (event: any) => {
    switch (event.event_type) {
      case TwoWayConversationMessageEvents.messageAdded:
        messageConversationAddedHandler(event.event);
        return;
      case TwoWayConversationClientEvents.messageUpdated:
        messageConversationUpdatedHandler(event.event);
        return;
      case TwoWayConversationMessageEvents.typingStarted:
        typingConversationStartedHandler(event.event);
        return;
      case TwoWayConversationMessageEvents.typingEnded:
        typingConversationEndedHandler(event.event);
        return;
      case TwoWayConversationClientEvents.conversationRemoved:
        if (
          (event &&
            event.event &&
            event.event.sid &&
            selectedChannelRef.current &&
            event.event.sid === selectedChannelRef.current) ||
          !event
        ) {
          selectedChannelRef.current = null;
        }
        return;
      case TwoWayConversationClientEvents.conversationLeft:
        if (
          event &&
          event.event &&
          event.event.sid &&
          selectedChannelRef.current &&
          event.event.sid === selectedChannelRef.current
        ) {
          setCustomerInfo({});
          setConversation();
        }
        if (
          (event &&
            event.event &&
            event.event.sid &&
            selectedChannelRef.current &&
            event.event.sid === selectedChannelRef.current) ||
          !event
        ) {
          selectedChannelRef.current = null;
        }
        setRefreshConversations(true); // refresh conversations list
        return;
      case TwoWayConversationClientEvents.conversationJoined:
        conversationJoinedHandler(event.event);
        return;
      case TwoWayConversationClientEvents.conversationUpdated:
        conversationUpdatedHandler(event.event);
        return;
      case TwoWayConversationClientEvents.participantJoined:
        participantJoinedHandler(event.event);
        return;
      case TwoWayConversationClientEvents.participantUpdated:
        participantUpdatedHandler(event.event);
        return;
      case TwoWayConversationClientEvents.participantLeft:
        participantLeftHandler(event.event);
        return;
    }
  };

  const participantUpdatedHandler = useCallback(
    async (event: ParticipantUpdatedEvent) => {
      const { participant, updateReasons } = event;
      if (
        clientRef.current &&
        participant.conversation.sid === selectedChannelRef.current
      ) {
        if (updateReasons.indexOf('lastReadMessageIndex') > -1) {
          TwoWayService.setAllMessagesConsumed(participant.conversation);
        }
      }
    },
    [selectedChannelRef, clientRef, clientId],
  );

  /**
   * Listen for updates to agents state:
   * - agent joining conversation -> should open that conversation in an assigned tab -> consume all messages
   */
  const participantJoinedHandler = useCallback(
    async (participant: Participant) => {
      if (
        clientRef.current &&
        participant.conversation.sid === selectedChannelRef.current
      ) {
        fetchAssignedAgents(clientRef.current, participant.conversation.sid);
        setIsAgentJoined(true);
        TwoWayService.setAllMessagesConsumed(participant.conversation);
      }
    },
    [selectedChannelRef, clientRef, isAgentJoined],
  );

  /**
   * Listen for updates to agents state:
   * - agent leaving conversation
   */
  const participantLeftHandler = useCallback(
    async (participant: Participant) => {
      if (
        clientRef.current &&
        participant.conversation.sid === selectedChannelRef.current
      ) {
        fetchAssignedAgents(clientRef.current, participant.conversation.sid);
      }
    },
    [selectedChannelRef, clientRef],
  );

  /**
   *  Listen for updates to agents state:
   * - agent status change
   */
  const conversationUpdatedHandler = useCallback(
    async ({ conversation, updateReasons }) => {
      if (
        selectedChannelRef.current &&
        conversation.sid === selectedChannelRef.current
      ) {
        if (
          updateReasons.includes('dateUpdated') ||
          updateReasons.includes('lastMessage')
        ) {
          fetchAssignedAgents(clientRef.current, selectedChannelRef.current);
        }
        if (!updateReasons.includes('attributes')) return;

        const {
          customer_language: customerLanguage,
          translations_enabled: translationsEnabled,
        } = conversation.attributes;
        const currentChannelInfo = channelInfoRef.current;
        const hasChanged =
          (customerLanguage &&
            currentChannelInfo?.customerLanguage !== customerLanguage) ||
          (translationsEnabled != undefined &&
            currentChannelInfo?.translationsEnabled !== translationsEnabled);

        if (hasChanged && selectedChannelRef.current && clientId) {
          await getChannelInfo();
          if (
            isRMTranslationsEnabled &&
            isAssignedViewRef.current &&
            translationsEnabled
          ) {
            await translateCustomerMessages(
              messageRef.current,
              conversation.sid,
            );
          }
        }
      }
    },
    [clientId],
  );

  /**
   * Keep track of newly assigned conversations agents
   */
  const conversationJoinedHandler = useCallback(
    async (conversation: Conversation) => {
      if (conversation.sid) {
        let currentAgentJoined = false;
        const participants = await conversation.getParticipants();
        if (!participants) return;
        const currentUser = participants.find(
          (participant) =>
            participant.identity === `agent::${clientId}::${userId}`,
        );
        if (
          clientRef.current &&
          conversation.sid === selectedChannelRef.current
        ) {
          // exclude agent that just joined conversation
          currentAgentJoined = true;
        }

        const attributes = conversation.attributes;
        if (participants.length >= 2) {
          const initiatedBy = attributes['initiatedBy'];
          const lastReadTimestamp = currentUser.lastReadTimestamp;
          if (
            currentUser &&
            lastReadTimestamp === null &&
            initiatedBy &&
            initiatedBy !== userId &&
            !currentAgentJoined
          ) {
            setRefreshConversations(true); // update conversations list
            newConversationNotifier();
            setNewlyAssignedConvSids((val) => [...val, conversation.sid]);
          }
        }
      }
    },
    [clientRef.current, userId, selectedChannelRef.current],
  );

  const typingConversationStartedHandler = useCallback(
    async (event: Participant) => {
      if (event.conversation.sid === selectedChannelRef.current) {
        const displayName = await TwoWayService.getDisplayName(
          event.identity,
          clientRef.current,
        );
        setTypingMessage(`${displayName} is typing...`);
      }
    },
    [selectedChannelRef, clientRef],
  );

  const typingConversationEndedHandler = (event: Participant) => {
    if (event.conversation.sid === selectedChannelRef.current) {
      setTypingMessage(null);
    }
  };

  useEffect(() => {
    let clientObj: Client | null = null;
    if (agentJWT.length) {
      clientObj = new Client(agentJWT);
      clientObj.on('stateChanged', (state) => {
        if (state === 'initialized') {
          setTwilioClient(clientObj);
          clientRef.current = clientObj;
          TwoWayService.listenToClientMessages(clientObj, (callback) => {
            channelConversationEventRouter(callback);
          });
        } else if (state === 'failed') {
          console.error('Failed to initialize Twilio client');
        }
      });
    }
    return () => {
      clientObj?.removeAllListeners();
    };
  }, [agentJWT]);

  const setConversation = (channelSid?: string, isAgent?: boolean) => {
    setMyConversationsActivity(false); // clear notification activity
    if (isAgent) {
      setIsAgentJoined(true);
    }
    if (channelSid) {
      // join conversation case
      selectedChannelRef.current = channelSid;
      loadConversation();
    }
  };

  const assignedConversationsCount = (
    <span>
      {myConversationsActivity ? (
        <Badge color="negative">{allAssignedChats?.length ?? 0}</Badge>
      ) : (
        `(${allAssignedChats?.length ?? 0})`
      )}
    </span>
  );

  const getUserInfo = useCallback(async () => {
    // UserService calls a cdm endpoint that returns a Customer object that doesn't have all the
    // information that's available.
    if (clientId != null && customerId != null) {
      try {
        const customerDetails = await UserService.getCustomerDetails(
          clientId,
          customerId,
        );
        setCustomer(customerDetails);
        if (customerDetails?.ccid != null) {
          // So we have to call this endpoint to get the rest of it.
          const twoWayCustomer = await UserService.getCustomerDetailsByCcid(
            clientId,
            customerDetails.ccid
          );
          setCustomer(twoWayCustomer);
        }
      } catch (error) {
        console.error(`Failed to get customer details: ${error}`);
      }
    }
  }, [clientId, customerId]);

  useEffect(() => {
    getUserInfo();
  }, [clientId, customerId]);

  // add full name to the user info
  const getCurrentUser = useCallback(async () => {
    const identity = `agent::${clientId}::${userId}`;
    try {
      const userInfo = await TwoWayService.getUserInfo(twilioClient, identity);
      setUserInfo({ ...userInfo, userRoleIds }); // don't miss to add userRoleIds, user identity doesn't have this information
    } catch (error) {
      console.error(`Failed to get current user: ${error}`);
    }
  }, [twilioClient, clientId, userId]);

  useEffect(() => {
    if (twilioClient && clientId && userId) {
      getCurrentUser();
    }
  }, [twilioClient, clientId, userId]);

  // agent conversations are loaded when agent view is selected or when route has agent id, in that case we load the conversations for that specific agent
  const loadAgentConversations = useCallback(
    async (agentId: string) => {
      try {
        if (agentId && clientId) {
          setIsAgentConversationsLoading(true);
          setAllAgentsChats([]);
          const conversations: ParticipantConversation[] =
            await TwoWayService.loadAgentConversation(agentId, clientId);
          setAllAgentsChats(conversations);
        }
      } catch (error) {
        console.error('Error loading agent conversations', error);
      } finally {
        setIsAgentConversationsLoading(false);
      }
    },
    [clientId],
  );

  useEffect(() => {
    if (!isAgentView || !routeAgentId) {
      return;
    }

    setSelectedAgentInfo((currentSelectedAgent) =>
      currentSelectedAgent?.id === routeAgentId ? currentSelectedAgent : null,
    );
    loadAgentConversations(routeAgentId);
  }, [isAgentView, routeAgentId, loadAgentConversations]);

  const conversationsClicked = {
    isMyConversationsClicked: isAssignedView,
    IsMyUnassignedConversationClicked: isUnassignedView,
    IsMyClosedConversationsClicked: isClosedView,
    agentConversationsClicked: isAgentView,
  };

  const { isSelectModeActive } = useBulkSelection();
  // mask the chat panel as soon as Select mode is entered, independent of current selection count
  const isUnassignedBulkSelectionActive = isUnassignedView && isSelectModeActive;

  // filter our agent and system messages and translate only the 50 latest customer messages
  const translateCustomerMessages = useCallback(
    async (messages: any[], channelSid: string) => {
      try {
        const res = await TwoWayService.enableTranslations(
          clientId,
          channelSid,
          messages
            .slice(-50)
            .filter(
              (msg) =>
                !checkAuthorIsAgent(msg.author) &&
                msg.author != 'system' &&
                !msg.versions?.translations['en']?.text,
            )
            .map((msg) => {
              return { messageSid: msg.sid, text: msg.body };
            }),
        );
      } catch (error) {
        console.error('Error translating message:', error);
        throw error;
      }
    },
    [clientId, channelSid, message],
  );

  const onTranslateConversation = useCallback(async () => {
    if (channelInfo?.translationsEnabled) {
      try {
        //turn translation off
        await TwoWayService.turnOnOffTranslations(clientId, channelSid, false);
        setEnableTranslationError({
          error: false,
          message: '',
        });
      } catch (error) {
        setEnableTranslationError({
          error: true,
          message: error.response.data.message,
        });
      }
    } else {
      try {
        //turn translation on
        await translateCustomerMessages(message, channelSid);
        await TwoWayService.turnOnOffTranslations(clientId, channelSid, true);
      } catch (error) {
        setEnableTranslationError({
          error: true,
          message: error.response.data.message,
        });
      }
    }
  }, [channelInfo, clientId, channelSid, translateCustomerMessages]);

  const composerRef = useRef<HTMLDivElement>(null);

  const fetchClosedReasonsRef = useRef(fetchClosedReasons);
  const setAssignedAgentsRef = useRef(setAssignedAgents);
  useEffect(() => {
    fetchClosedReasonsRef.current = fetchClosedReasons;
  }, [fetchClosedReasons]);

  useEffect(() => {
    setAssignedAgentsRef.current = setAssignedAgents;
  }, [setAssignedAgents]);

  useEffect(() => {
    setIsAgentJoined(false); // reset agent joined state on conversation type change TODO: to be removed once we have a better way to handle agent joined state instead of relying on conversation type change

    if (isUnassignedView) {
      // reset assigned agents on unassigned view click as the assigned agents are relevant to assigned conversations only TODO: to be removed once we have a better way to handle assigned agents state instead of relying on conversation type change
      setAssignedAgentsRef.current([]);
    }
    if (isClosedView) {
      // get closed reasons if closed view is clicked and closed reasons are enabled
      fetchClosedReasonsRef.current(clientId); // TODO: to be removed once we have a better way to handle closed reasons state instead of relying on conversation type change, check RelayMessengerCustomerInfo, maybe we don't need another fetchClosedReasons call there and can rely on this one to set the closed reasons state for the closed view
    }
  }, [conversationType, clientId, isClosedView, isUnassignedView]);

  // Routing
  const navigate = useNavigate();
  const routeResolutionAttemptRef = useRef('');
  const appliedRouteSelectionRef = useRef('');

  const getRouteConversationMatch = useCallback(() => {
    if (!routeChannelSid) {
      return null;
    }

    switch (conversationType) {
      case 'assigned':
        return allAssignedChats?.find((chat) => chat.sid === routeChannelSid);
      case 'unassigned':
        return allUnassignedChats?.find(
          (chat) => chat.channel_sid === routeChannelSid,
        );
      case 'closed':
        return closedConversations?.find(
          (chat) => chat.channel_sid === routeChannelSid,
        );
      case 'agent':
        return allAgentsChats?.find((chat) => chat.sid === routeChannelSid);
      default:
        return null;
    }
  }, [
    allAgentsChats,
    allAssignedChats,
    allUnassignedChats,
    closedConversations,
    conversationType,
    routeChannelSid,
  ]);

  const getRouteChatType = useCallback(() => {
    switch (conversationType) {
      case 'assigned':
        return RelayMessengerChatTypes.MyConversations;
      case 'unassigned':
        return RelayMessengerChatTypes.UnassignedConversations;
      case 'closed':
        return RelayMessengerChatTypes.ClosedConversations;
      case 'agent':
        return RelayMessengerChatTypes.AgentConversations;
      default:
        return RelayMessengerChatTypes.MyConversations;
    }
  }, [conversationType]);

  const navigateForMissingRouteConversation = useCallback(
    (isFirstAttemptForKey: boolean) => {
      if (isAgentView && isAgentConversationsLoading) {
        return;
      }

      if (isAssignedView && isAssignedConversationsLoading) {
        return;
      }

      if (
        isAssignedView &&
        joinNavigationTargetSid &&
        routeChannelSid === joinNavigationTargetSid
      ) {
        return;
      }

      // Avoid redirecting on the initial run before conversations have had a chance to load.
      if (isFirstAttemptForKey) {
        return;
      }

      if (isUnassignedView) {
        showMessage(
          `This conversation cannot be opened because it's already assigned to
          another agent.`,
          'error',
        );
        navigate(`/${clientId}/unassigned`, {
          replace: true,
        });
        return;
      }
      // otherwise, navigate to assigned conversations
      navigate(`/${clientId}/assigned`, { replace: true });
    },
    [
      clientId,
      isAssignedConversationsLoading,
      isAssignedView,
      isAgentConversationsLoading,
      isAgentView,
      isUnassignedView,
      joinNavigationTargetSid,
      navigate,
      routeChannelSid,
    ],
  );

  // TODO: combine this logic with the one from RM list on each conversation type click
  const applyRouteConversationSelection = useCallback(
    (match: any, resolutionKey: string) => {
      const info = getCustomerInfoByChatType(match, getRouteChatType());
      const matchChannelSid = match.sid ?? match.channel_sid;
      const appliedRouteSelectionKey = `${resolutionKey}:${matchChannelSid}`;

      if (
        appliedRouteSelectionRef.current === appliedRouteSelectionKey &&
        selectedChannelRef.current === routeChannelSid
      ) {
        return;
      }

      appliedRouteSelectionRef.current = appliedRouteSelectionKey;
      const resolvedTagName =
        info?.tagId && hasLoadedAgentTags && agentTags.length > 0
          ? getTagNameById(agentTags, info?.tagId)
          : null;

      setCustomerInfo({
        ...info,
        ...(resolvedTagName ? { tagName: resolvedTagName } : {}),
        dateCreated: match.dateCreated ?? match.created_at,
        dateUpdated: match.dateUpdated ?? match.updated_at,
      });

      if (selectedChannelRef.current !== routeChannelSid) {
        setConversation(routeChannelSid);
      }
    },
    [
      getRouteChatType,
      routeChannelSid,
      setCustomerInfo,
      agentTags,
      hasLoadedAgentTags,
    ],
  );

  // Support deep linking to a conversation based on the URL.
  useEffect(() => {
    if (!routeChannelSid) return;

    const resolutionKey = `${conversationType}:${routeChannelSid}`;
    const isFirstAttemptForKey =
      routeResolutionAttemptRef.current !== resolutionKey;
    routeResolutionAttemptRef.current = resolutionKey;

    const match = getRouteConversationMatch();
    if (!match) {
      // skip the not-found redirect when the current route matches the already-selected channel in Unassigned/Closed views.
      if (
        (isUnassignedView || isClosedView) &&
        selectedChannelRef.current === routeChannelSid
      ) {
        return;
      }
      navigateForMissingRouteConversation(isFirstAttemptForKey);
      return;
    }

    applyRouteConversationSelection(match, resolutionKey);
  }, [
    applyRouteConversationSelection,
    conversationType,
    getRouteConversationMatch,
    isClosedView,
    isUnassignedView,
    navigateForMissingRouteConversation,
    routeChannelSid,
  ]);

  return (
    <>
      <FlexGrid
        flexGridColumnCount={4}
        overrides={{
          Block: {
            style: () => ({
              justifyContent: 'space-between',
              alignItems: 'center',
              backgroundColor: '#F5F6F6',
              position: 'fixed',
              top: '61px',
              left: '198px',
              right: 0,
              bottom: 0,
              paddingTop: '10px',
              paddingBottom: '10px',
            }),
          },
        }}
        id="relay-messenger-home"
      >
        <FlexGridItem
          overrides={{
            Block: {
              style: () => ({
                width: `15%`,
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
              }),
            },
          }}
        >
          <div
            className={css({
              marginTop: '20px',
            })}
          >
            <AgentAvatar
              name={userFullName ? userFullName : 'Loading..'}
              status={agentStatus}
            ></AgentAvatar>
          </div>
          <hr
            className={css({
              width: '200px',
              top: '93px',
            })}
          ></hr>
          <div
            className={css({
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'flex-start',
              marginLeft: '20px',
            })}
          >
            <RelayMessengerNavLink
              to={`/${clientId}/assigned`}
              activeFontWeight="700"
              inactiveFontWeight="400"
            >
              <div
                className={css({
                  display: 'flex',
                })}
                data-testid={TwoWayTestId.MyConversations}
                onClick={() => {
                  setCustomerInfo({});
                  setConversation();
                }}
              >
                <p
                  className={css({
                    marginRight: '10px',
                  })}
                >
                  My Conversations
                </p>
                {assignedConversationsCount}
              </div>
            </RelayMessengerNavLink>
            <RelayMessengerNavLink
              to={`/${clientId}/unassigned`}
              activeFontWeight="700"
              inactiveFontWeight="400"
            >
              <div
                data-testid={TwoWayTestId.UnassignedConversations}
                className={css({
                  display: 'flex',
                })}
                onClick={() => {
                  setCustomerInfo({});
                  setIsAgentJoined(false);
                  fetchLaunchedByList(clientId);
                }}
              >
                <p
                  className={css({
                    marginRight: '10px',
                  })}
                >
                  Unassigned
                </p>
                <span>{`(${totalUnassignedChats ?? 0})`}</span>
              </div>
            </RelayMessengerNavLink>
            <RelayMessengerNavLink
              to={`/${clientId}/closed`}
              activeFontWeight="700"
              inactiveFontWeight="400"
            >
              <div
                data-testid={TwoWayTestId.ClosedConversations}
                className={css({
                  display: 'flex',
                })}
                onClick={() => {
                  setCustomerInfo({});
                  setIsAgentJoined(false);
                }}
              >
                <p
                  className={css({
                    marginRight: '10px',
                  })}
                >
                  Closed
                </p>
              </div>
            </RelayMessengerNavLink>
            {isCurrentUserAnAdmin && (
              <RelayMessengerAgents
                agentConversationsClicked={isAgentView}
                selectedAgentId={selectedAgentId}
                onAgentSelected={(agent) => {
                  if (agent && agent.id) {
                    setIsAgentJoined(false);
                    setSelectedAgentInfo(agent);
                    setCustomerInfo({});
                  }
                }}
                onAgentResolved={setSelectedAgentInfo}
              />
            )}
          </div>
        </FlexGridItem>
        <FlexGridItem
          overrides={{
            Block: {
              style: () => ({
                width: `25%`,
                height: `100%`,
                backgroundColor: 'white',
                borderTopLeftRadius: '10px',
                borderBottomLeftRadius: '10px',
                borderTopRightRadius: '10px',
                borderBottomRightRadius: '10px',
              }),
            },
          }}
        >
          <RelayMessengerConversations
            chatsArray={{
              setAllAssignedChats,
              allAssignedChats,
              allUnassignedChats,
              setAllUnassignedChats,
              setClosedConversations,
              closedConversations,
              allAgentsChats,
            }}
            conversationsClicked={conversationsClicked}
            isAgentJoined={isAgentJoined}
            selectedAgentInfo={selectedAgentInfo}
            isRMTranslationsEnabled={isRMTranslationsEnabled}
            setIsAssignedConversationsLoading={
              setIsAssignedConversationsLoading
            }
            setTotalUnassignedChats={setTotalUnassignedCount}
            setTotalClosedConversations={setTotalClosedCount}
            totalUnassignedChats={totalUnassignedChats}
            totalClosedConversations={totalClosedConversations}
          />
        </FlexGridItem>
        <FlexGridItem
          overrides={{
            Block: {
              style: () => ({
                width: `38%`,
                height: `100%`,
                display: 'flex',
                flexDirection: 'column',
                backgroundColor: 'white',
                borderTopLeftRadius: '10px',
                borderBottomLeftRadius: '10px',
                borderTopRightRadius: '10px',
                borderBottomRightRadius: '10px',
                marginLeft: '5px',
                overflow: 'hidden',
              }),
            },
          }}
        >
          {typeof showCustomerOrAgentInfo === 'string' &&
            showCustomerOrAgentInfo.length > 0 && (
              <>
                <div
                  style={{
                    flexShrink: 0,
                  }}
                >
                  <TwoWayMessagingCustomerInfo
                    conversationsClicked={conversationsClicked}
                    setAgentStatus={setAgentStatus}
                    setIsPingTriggered={setIsPingTriggered}
                    customerCcid={customer?.ccid}
                    selectedChannelRef={selectedChannelRef}
                  />
                  {showCustomerStatus && (
                    <CustomerStatusBadge
                      content={`${customerFullName} is ${customerStatus}`}
                      variant={
                        customerStatus === 'online' ? 'primary' : 'secondary'
                      }
                    ></CustomerStatusBadge>
                  )}
                </div>
                <div
                  className={css({
                    position: 'relative',
                    display: 'flex',
                    flexDirection: 'column',
                    flex: 1,
                    minHeight: 0,
                  })}
                >
                  <TwoWayChatCard
                    chatMessages={message}
                    conversationsClicked={conversationsClicked}
                    typingMessage={typingMessage}
                    lastMessageLoaded={lastMessageLoaded}
                    clientId={clientId}
                    isRMAutoResponseEnabled={isRMAutoResponseEnabled}
                    isRMTranslationsEnabled={
                      isRMTranslationsEnabled && !isUnassignedView
                    }
                    isConversationTranslated={channelInfo?.translationsEnabled}
                    onTranslateConversation={onTranslateConversation}
                    enableTranslationError={enableTranslationError}
                    composerRef={composerRef}
                  ></TwoWayChatCard>
                  {isUnassignedBulkSelectionActive && (
                    <div
                      data-testid="bulk-select-message-mask"
                      className={css({
                        position: 'absolute',
                        inset: '0',
                        backdropFilter: 'blur(6px)',
                        backgroundColor: 'rgba(255, 255, 255, 0.6)',
                      })}
                    ></div>
                  )}
                </div>
                {isAssignedView && (
                  <div
                    ref={composerRef}
                    style={{
                      flexShrink: 0,
                    }}
                  >
                    <TwoWayChatInput
                      onTyping={sendTyping}
                      onNewMessage={sendAgentMessage}
                      onTranslateMessage={translateAgentMessage}
                      translation={translation}
                      setTranslation={setTranslation}
                      isConversationTranslated={
                        channelInfo?.translationsEnabled && isAssignedView
                      }
                      customerLanguage={channelInfo?.customerLanguage}
                      isRMTranslationsEnabled={
                        isRMTranslationsEnabled && isAssignedView
                      }
                      onTurnOffTranslation={onTranslateConversation}
                    ></TwoWayChatInput>
                  </div>
                )}
              </>
            )}
        </FlexGridItem>
        <FlexGridItem
          {...itemProps}
          overrides={{
            Block: {
              style: () => ({
                width: `20%`,
                height: '100%',
                justifyContent: 'center',
                overflowY: 'auto',
              }),
            },
          }}
        >
          <TwoWayMessagingAdditionalInfo
            conversationsClicked={conversationsClicked}
            setIsAgentJoined={setIsAgentJoined}
            loadAgentConversations={loadAgentConversations}
            status={agentStatus}
            setAgentStatus={setAgentStatus}
            customer={customer}
            setCustomer={setCustomer}
            allAssignedChats={allAssignedChats}
            allUnassignedChats={allUnassignedChats}
            closedConversations={closedConversations}
            isPingTriggered={isPingTriggered}
            showCustomerOrAgentInfo={
              typeof showCustomerOrAgentInfo === 'string' &&
              showCustomerOrAgentInfo.length > 0 &&
              showCustomerOrAgentInfo
            }
            allAgentsChats={allAgentsChats}
            selectedAgentId={selectedAgentId}
            selectedAgentFullName={selectedAgentFullName}
            joinNavigationTargetSid={joinNavigationTargetSid}
            setJoinNavigationTargetSid={setJoinNavigationTargetSid}
            setIsConversationTranslated={(value: boolean) => {
              setChannelInfo((prev) => ({
                ...prev,
                translationsEnabled: value,
              }));
            }}
          ></TwoWayMessagingAdditionalInfo>
        </FlexGridItem>
      </FlexGrid>
      <audio ref={audioRef}>
        <source src="/assets/audio/ping-bing.wav" type="audio/wav" />
        <source src="/assets/audio/ping-bing.ogg" type="audio/ogg" />
      </audio>
    </>
  );
};
